import { getFeatureFlagValue } from '@citrite/feature-flags';
import { generateRID } from '@sharefile/rid-utils';
import { PageComponentProps, PiletApi } from '@sharefiledev/sharefile-appshell';
import {
	internalWorkflowsPiletOverlays,
	workflowsPiletExtensionSlots,
	workflowsPiletOverlays,
} from '@sharefiledev/workflows-pilet-sdk';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import localeData from 'dayjs/plugin/localeData';
import weekday from 'dayjs/plugin/weekday';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import weekYear from 'dayjs/plugin/weekYear';
import { registerBlocks } from './blocks';
import { AppContextProvider } from './common';
import {
	configureDominoApiClient,
	configureRightSignatureApiClient,
	getResourceRegistryClient,
} from './data/clients';
import {
	AsyncAutomationsButton,
	AsyncAutomationsDropDownLabel,
	AsyncContextualActions,
	AsyncContextualWorkflowList,
	AsyncLoadFlowsByTrigger,
	AsyncProgressEvents,
} from './extensions/Extensions';
import { configureLaunchFlowExtensions } from './extensions/launchFlowExtensions';
import { FeatureFlag } from './FeatureFlags';
import { configureFileExportAppShellExtension } from './FileExport/extensions/configureFileExportAppShellExtension';
import { configureFileExportProjectsExtension } from './FileExport/extensions/configureFileExportProjectsExtension';
import { configureFormExtensions } from './Form';
import { setLogger } from './logger';
import { registerNonProdDevPages } from './nonProdDevPages';
import {
	AsyncStorageFileExportModal,
	AutomationsOverlay,
	DesignFlowOverlay,
	getDefaultDrawerProps,
	getDefaultModalProps,
	LaunchFlowOverlay,
	RunDetailsOverlay,
} from './overlays';
import { AsyncDashboardPage } from './Pages';
import {
	checkRolesAndPreferences,
	isVdr,
	RenderConditionally,
	RolesAndProvisioningRequirements,
} from './provisioning';
import { workflowListPath } from './routes';
import { configureStartFlowContextualExtensions } from './StartFlow';
import WorkflowsContextProvider from './useWorkflowsContext';
import { isEUAccount, t, withDefaultProviders } from './util';

dayjs.extend(customParseFormat);
dayjs.extend(advancedFormat);
dayjs.extend(weekday);
dayjs.extend(localeData);
dayjs.extend(weekOfYear);
dayjs.extend(weekYear);

export function setup(app: PiletApi) {
	setLogger(app.sf.getLogger());

	if (isEUAccount() && !getFeatureFlagValue(FeatureFlag.EnableWorkflowsPiletForEU)) {
		return;
	}

	configureApiClients(app);

	registerLeftNavEntries(app);
	registerDashboard(app);
	registerOverlays(app);
	registerInternalOverlays(app);
	registerExtensions(app);
	handleDevToolingRegistrations(app);
	registerBlocks(app);
}

const handleDevToolingRegistrations = (app: PiletApi) => {
	if (process.env.NODE_ENV === 'development') {
		registerNonProdDevPages(app);
	}
};

const getRolesProvisioningRequirements = (app: PiletApi) => {
	const requirements: RolesAndProvisioningRequirements = {
		requiredRoles: ['Employee'],
		requiredASTs: ['EnableAutomatedWorkflows'],
	};

	if (isVdr(app)) {
		requirements.requiredLaunchDarklyFeatureFlags = [
			FeatureFlag.EnableAutomatedWorkflowsVDR,
		];
	}

	return requirements;
};

function registerLeftNavEntries(app: PiletApi) {
	app.sf.registerLeftNavChildComponent({
		parent: '/workflows',
		href: workflowListPath,
		title: () => t('workflows-pilet:titles.dashboard') ?? '...',
		isAvailable: () =>
			checkRolesAndPreferences(app, getRolesProvisioningRequirements(app)),
		weight: 1,
	});
}

function configureApiClients(app: PiletApi) {
	configureDominoApiClient(app);
	configureRightSignatureApiClient();
}

function registerDashboard(api: PiletApi) {
	const DashboardComponent = (props: PageComponentProps) => (
		<RenderConditionally
			{...props}
			piletApi={api}
			requirements={getRolesProvisioningRequirements(api)}
			redirectToDashboard
		>
			<AsyncDashboardPage {...props} piletApi={api} />
		</RenderConditionally>
	);
	api.registerPage(workflowListPath, DashboardComponent);
}

function registerOverlays(piletApi: PiletApi) {
	const { user, account } = piletApi.sf.piletUserContext.get();

	piletApi.registerModal(
		workflowsPiletOverlays.launchFlow,
		withDefaultProviders(piletApi, props => (
			<WorkflowsContextProvider value={{ user, account }}>
				<LaunchFlowOverlay {...props} {...props.options} />
			</WorkflowsContextProvider>
		)),
		getDefaultModalProps({
			titleText: t('workflows-pilet:user_actions.start_flow'),
			isFullscreen: true,
		})
	);

	piletApi.registerDrawer(
		workflowsPiletOverlays.designFlow,
		withDefaultProviders(piletApi, props => (
			<WorkflowsContextProvider
				value={{
					user,
					account,
					resourceRegistryClient: getResourceRegistryClient(),
				}}
			>
				<DesignFlowOverlay {...props} {...props.options} />
			</WorkflowsContextProvider>
		)),
		getDefaultDrawerProps({
			titleText: t('workflows-pilet:user_actions.add_automation'),
			size: 'medium',
		})
	);

	piletApi.registerModal(
		workflowsPiletOverlays.fileExport,
		withDefaultProviders(
			piletApi,
			({ options, cancelInteraction, completeInteraction }) => (
				<AsyncStorageFileExportModal
					cancelInteraction={cancelInteraction}
					completeInteraction={completeInteraction}
					{...options}
				/>
			)
		),
		{
			modalProps: {
				title: t('workflows-pilet:file_export.export'),
				destroyOnClose: true,
				className: 'modal--fileExport',
				footer: false,
			},
		}
	);

	piletApi.registerDrawer(
		workflowsPiletOverlays.automations,
		withDefaultProviders(piletApi, props => (
			<WorkflowsContextProvider value={{ user, account }}>
				<AutomationsOverlay {...props} {...props.options} />
			</WorkflowsContextProvider>
		)),
		{
			drawerProps: {
				width: '100%',
				title: t('workflows-pilet:common.automations'),
			},
		}
	);
}

function registerInternalOverlays(piletApi: PiletApi) {
	const { user, account } = piletApi.sf.piletUserContext.get();

	piletApi.registerModal(
		internalWorkflowsPiletOverlays.runDetails,
		withDefaultProviders(piletApi, props => (
			<WorkflowsContextProvider value={{ user, account }}>
				<RunDetailsOverlay {...props} {...props.options} />
			</WorkflowsContextProvider>
		)),
		getDefaultModalProps({
			titleText: t('workflows-pilet:common.run_details'),
			isFullscreen: true,
		})
	);
}

function registerExtensions(app: PiletApi) {
	const { user, account } = app.sf.piletUserContext.get();
	configureStartFlowContextualExtensions(app);
	configureFormExtensions(app);
	configureFileExportAppShellExtension(app);
	configureFileExportProjectsExtension(app);
	if (getFeatureFlagValue(FeatureFlag.EnableMarketplace)) {
		configureLaunchFlowExtensions(app);
	}

	app.registerExtension(
		workflowsPiletExtensionSlots.contextualWorkflowList,
		withDefaultProviders(app, ({ params }) => (
			<AppContextProvider value={{ piletApi: app }}>
				<WorkflowsContextProvider value={{ user, account }}>
					<AsyncContextualWorkflowList {...params} />
				</WorkflowsContextProvider>
			</AppContextProvider>
		)),
		{
			containerRID: generateRID({ baseEntityId: account.Id ?? '' }),
			showSimpleView: true,
		}
	);

	app.registerExtension(
		workflowsPiletExtensionSlots.contextualActions,
		withDefaultProviders(app, ({ params }) => (
			<AppContextProvider value={{ piletApi: app }}>
				<WorkflowsContextProvider value={{ user, account }}>
					<AsyncContextualActions {...params} />
				</WorkflowsContextProvider>
			</AppContextProvider>
		))
	);

	app.registerExtension(
		workflowsPiletExtensionSlots.loadFlowsByTrigger,
		withDefaultProviders(app, ({ params }) => (
			<AppContextProvider value={{ piletApi: app }}>
				<WorkflowsContextProvider value={{ user, account }}>
					<AsyncLoadFlowsByTrigger {...params} />
				</WorkflowsContextProvider>
			</AppContextProvider>
		))
	);

	app.registerExtension(
		workflowsPiletExtensionSlots.automationsButton,
		withDefaultProviders(app, ({ params }) => (
			<AppContextProvider value={{ piletApi: app }}>
				<WorkflowsContextProvider value={{ user, account }}>
					<AsyncAutomationsButton {...params} />
				</WorkflowsContextProvider>
			</AppContextProvider>
		))
	);

	app.registerExtension(
		workflowsPiletExtensionSlots.automationsDropDownLabel,
		withDefaultProviders(app, ({ params }) => (
			<AppContextProvider value={{ piletApi: app }}>
				<WorkflowsContextProvider value={{ user, account }}>
					<AsyncAutomationsDropDownLabel {...params} />
				</WorkflowsContextProvider>
			</AppContextProvider>
		))
	);

	app.registerExtension(
		workflowsPiletExtensionSlots.progressEvents,
		withDefaultProviders(app, ({ params }) => (
			<AppContextProvider value={{ piletApi: app }}>
				<WorkflowsContextProvider value={{ user, account }}>
					<AsyncProgressEvents {...params} />
				</WorkflowsContextProvider>
			</AppContextProvider>
		))
	);
}
